import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HelpCircle, Map, Navigation, RotateCcw, Bug } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { InlineSearchDropdown } from "./InlineSearchDropdown";
import { QuickAddDropdown } from "./QuickAddDropdown";
import { LeadCreateModal } from "@/components/leads/LeadCreateModal";
import { CompanyCreateModal } from "@/components/companies/CompanyCreateModal";
import { DealCreateModal } from "@/components/deals/DealCreateModal";
import { ActivityCreateModal } from "@/components/activities/ActivityCreateModal";
import { BugReportModal } from "@/components/bug-report/BugReportModal";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { HeaderCallButton } from "./HeaderCallButton";
import { useTour } from "@/features/tour/TourContext";
import { TOUR_IDS } from "@/features/tour/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import logoVerttoBranco from "@/assets/logo-vertto-branco.svg";
import logoAtticusHorizontal from "@/assets/atticus/atticus-horizontal-papel.svg";
import { useOrgPreset } from "@/hooks/shared/useOrgPreset";

interface NavbarProps {
  className?: string;
}

export const Navbar = ({ className }: NavbarProps) => {
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [bugReportModalOpen, setBugReportModalOpen] = useState(false);
  const { startTour, restartTour, resetAllTours, getCurrentTourId } = useTour();
  // Org com preset: logo da marca. Com interface enxuta (`shell`, ex.: Atticus.hub) também some
  // o telefone do header (telefonia não faz parte do pacote) e os tours, que passeiam por
  // páginas que o shell bloqueia. Preset só de marca mantém os dois.
  const orgPreset = useOrgPreset();
  const isAtticus = orgPreset?.brandAttr === 'atticus';
  const interfaceEnxuta = Boolean(orgPreset?.shell);

  return (
    <nav
      className={cn(
        "w-full flex-shrink-0 backdrop-blur-none bg-transparent relative",
        className,
      )}
      style={{ zIndex: 100000 }}
    >
      <div className="flex h-16 items-center px-6">
        {/* Left side - Logo */}
        <div className="flex items-center min-w-0 mr-8">
          {isAtticus ? (
            // O logotipo é desenho fechado (brandbook §8: "nunca redigitar"). O ".hub" entra
            // como rótulo técnico separado, no registro de dado (IBM Plex Mono), sem colar na
            // marca. Lockup em Papel sobre a navbar Oliva: 12,89:1, combinação aprovada (§12).
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src={logoAtticusHorizontal} alt="Atticus" className="atticus-navbar-logo h-[22px]" />
              {orgPreset?.navbarTag && (
                <span className="atticus-navbar-hub" aria-hidden="true">{orgPreset.navbarTag}</span>
              )}
            </Link>
          ) : (
            <Link to="/dashboard">
              <img
                src={logoVerttoBranco}
                alt="Vertto"
                className="h-[22px]"
              />
            </Link>
          )}
        </div>

        {/* Center - Search */}
        <div className="flex-1 flex items-center justify-center max-w-2xl mx-auto px-8" data-tour="global-search">
          <div className="w-full max-w-md">
            <InlineSearchDropdown />
          </div>
        </div>

        {/* Right side - Notifications and Organization */}
        <div className="flex items-center space-x-4">
          {/* Quick Add */}
          <div data-tour="global-quick-add">
            <QuickAddDropdown
              onLeadCreate={() => setLeadModalOpen(true)}
              onCompanyCreate={() => setCompanyModalOpen(true)}
              onDealCreate={() => setDealModalOpen(true)}
              onActivityCreate={() => setActivityModalOpen(true)}
            />
          </div>

          {/* Telefone do header: webphone Sonax ou discagem pelo MicroSIP */}
          {!interfaceEnxuta && <HeaderCallButton />}

          {/* Notifications */}
          <div data-tour="global-notifications">
            <NotificationCenter />
          </div>

          {/* Help / Tour */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-foreground">
                    <HelpCircle className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">Ajuda</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              {!interfaceEnxuta && getCurrentTourId() && (
                <DropdownMenuItem onClick={() => {
                  const tourId = getCurrentTourId();
                  if (tourId) restartTour(tourId);
                }}>
                  <Navigation className="mr-2 h-4 w-4" />
                  <span>Tour desta página</span>
                </DropdownMenuItem>
              )}
              {!interfaceEnxuta && (
                <>
                  <DropdownMenuItem onClick={() => startTour(TOUR_IDS.GLOBAL)}>
                    <Map className="mr-2 h-4 w-4" />
                    <span>Tour completo</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => resetAllTours()}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    <span>Reiniciar tours</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => setBugReportModalOpen(true)}>
                <Bug className="mr-2 h-4 w-4" />
                <span>Relatar bug</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Organization Switcher */}
          <div data-tour="global-org-switcher">
            <OrganizationSwitcher />
          </div>
        </div>
      </div>

      <LeadCreateModal open={leadModalOpen} onOpenChange={setLeadModalOpen} />

      <CompanyCreateModal open={companyModalOpen} onOpenChange={setCompanyModalOpen} />

      <DealCreateModal open={dealModalOpen} onOpenChange={setDealModalOpen} />

      <ActivityCreateModal open={activityModalOpen} onOpenChange={setActivityModalOpen} />

      <BugReportModal open={bugReportModalOpen} onOpenChange={setBugReportModalOpen} />
    </nav>
  );
};
